<template>
	<div>
		<div
			class="drop-zone"
			:class="{ 'drag-over': isDragging }"			
			@dragover.prevent
			@dragenter.prevent="handleDragEnter"
			@dragleave.prevent="handleDragLeave"
			@drop.prevent="handleDrop"
		>
			<!-- Hidden file input - only triggered programmatically -->
			<input
				type="file"
				@change="handleFileSelect"
				ref="fileInput"
				style="display: none;"
				multiple
			/>
			<!-- Visible button to trigger file picker -->
			<button @click="openFilePicker" type="button" class="file-button">Choose Files</button>
			<!--  -->
			<p>Drop files here or click button to select</p>
			<div v-if="error" class="error">
				{{ error }}
			</div>
			
			<div v-if="thumbnails.length">
				<ul>
					<li v-for="(file, index) in files" :key="file.name" :class="{ 'uploaded': isFileUploaded(fileHashes[index]) }">
						<div class="thumb-wrapper">
							<img 
								v-if="isImageFile(file)"
								:src="thumbnails[index]"
								:alt="file.name"
								class="thumbnail"
								v-on:click="downloadFile(file)"
							/>
							<div v-else class="no-preview">No preview for this file type</div>
							<div v-if="isFileUploaded(fileHashes[index])" class="uploaded-badge">✓</div>
						</div>
						<div class="file-info">
							<span>{{ file.name }}</span>
							<!-- While checking, show a disabled checking state -->
							<button v-if="isCheckingUploads" disabled>Checking...</button>
							<!-- If already uploaded, show status only -->
							<span v-else-if="isFileUploaded(fileHashes[index])" class="uploaded-text">Uploaded</span>
							<!-- Otherwise show upload action -->
							<button v-else @click="handleFileUpload(file)" :disabled="isFileUploading(fileHashes[index])">{{ isFileUploading(fileHashes[index]) ? 'Uploading...' : 'Upload' }}</button>
						</div>
					</li>
				</ul>
			</div>
			<!-- Listed S3 images in user's protected prefix -->
			<div v-if="s3Images.length" class="s3-list-wrapper">
				<h4>Images in S3 (protected)</h4>
				<ul class="s3-list">
					<li v-for="img in s3Images" :key="img.key" class="s3-item">
						<span class="s3-key" :title="img.key">{{ img.key }}</span>
						<button class="s3-download" @click="downloadProtected(img.key)">Download</button>
					</li>
				</ul>
			</div>
		</div>
	</div>
</template>

 <script>
	import EXIF from 'exif-js';
	import CreateImageMutation from '..//graphql/server/Image/CreateImageMutation.gql';
	import CheckImagesQuery from '..//graphql/server/Image/CheckImagesQuery.gql';

	export default {
		name: 'ImagesView',
		data() {
			return {
				error: null,
				files: [],
				thumbnails: [],
				fileHashes: [],
				uploadProgress: 0,
				isDragging: false,
				session:{},
				authChecked:false,
				s3Images: [],
				isListing: false,
				isCheckingUploads: false,
				isBulkDownloading: false,
				identityId: null,
				uploadingFiles: new Set(),
				uploadedHashes: new Set(),
				recentlyUploadedHashes: new Set(),
				userID: null,
				syncResults: null,
				isAnalyzing: false,
				selectedResultFilter: 'all'
			}
		},
		props: {
			authStatus: {
				type: Object,
				required: true
			}
		},	
		methods: {
			async ensureIdentityId() {
				if (this.identityId) return this.identityId;
				try {
					// const session = await fetchAuthSession({ forceRefresh: true });
					// this.identityId = session.identityId; // Cognito Identity ID (matches folder name after protected/)
					this.identityId = this.authStatus.userID;
					// console.log('Fetched identityId:', this.identityId);
					return this.identityId;
				} catch (e) {
					this.error = 'Auth session fetch failed: ' + e.message;
					throw e;
				}
			},
			async computeFileHash(file) {
				const buffer = await file.arrayBuffer();
				const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
				const hashArray = Array.from(new Uint8Array(hashBuffer));
				const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
				return hashHex;
			},
			async listJpgs() {
				this.error = null;
				this.isListing = true;
				try {
					// const identityId = await this.ensureIdentityId();
					// console.log('Listing S3 images for identityId:', identityId);
					// Let Amplify prepend protected/<identityId>/ automatically
					// const { items } = await list({
					// 	path: `${identityId}/`,
					// 	options: { accessLevel: 'protected', defaultAuthMode: 'userPool' }
					// });
					// this.s3Images = items.filter(i => /\.jpe?g$/i.test(i.key));
					this.s3Images = [];
				} catch (e) {
					this.error = 'List failed: ' + (e.message || e);
					console.error('ListJpgs error detail:', e);
				} finally {
					this.isListing = false;
				}
			},
			async downloadProtected(key) {
				this.error = null;
				try {
					const token = localStorage.getItem('user-token') || sessionStorage.getItem('user-token');
					const baseUrl = process.env.VUE_APP_GRAPHQL_URL.replace('/graphql', '');
					const hash = key.split('.')[0];
					
					const response = await fetch(`${baseUrl}/api/file/${hash}`, {
						headers: {
							'Authorization': `Bearer ${token}`
						}
					});

					if (!response.ok) throw new Error('Failed to fetch image');

					const blob = await response.blob();
					const url = URL.createObjectURL(blob);
					const a = document.createElement('a');
					a.href = url;
					a.download = key.split('/').pop();
					document.body.appendChild(a);
					a.click();
					document.body.removeChild(a);
					URL.revokeObjectURL(url);
				} catch (e) {
					this.error = 'Download failed: ' + e.message;
				}
			},
			async createNeo4jImage(fileMetadata){
				// console.log('Create image -', fileMetadata);
				try{
					// Server owns the schema mapping (Nod IDs)
					// Client just sends the raw data
					const childNods = {
						ID: fileMetadata.ID,
						key: fileMetadata.key,
						fileName: fileMetadata.fileName,
						dateTaken: fileMetadata.dateTaken,
						dateCreated: fileMetadata.dateCreated,
						createdBy: fileMetadata.createdBy,
						ownerIdentityId: fileMetadata.ownerIdentityId,
						fileType: fileMetadata.type,
						fileSize: fileMetadata.size?.toString(),
						latitude: fileMetadata.latitude?.toString(),
						longitude: fileMetadata.longitude?.toString()
					};

					this.$apollo.mutate({
						mutation: CreateImageMutation,
						variables: {childNods},
					})
					.then (()=>{
						// console.log('CreateImage success:', data.data.CreateImage);
						// Add file hash to uploaded set after successful Neo4j save
						this.uploadedHashes.add(fileMetadata.ID);
						this.recentlyUploadedHashes.add(fileMetadata.ID); // Track for client-side fallback
						// console.log(`✅ Successfully created Neo4j entry for: ${fileMetadata.fileName}`);
					})
					.catch(error =>{
						console.error('CreateImageMutation error:', error);
						// Handle known duplicate error from server
						const duplicate = error?.graphQLErrors?.find(e => e?.extensions?.code === 'IMAGE_KEY_EXISTS');
						if (duplicate) {
							// This is a race condition - file was uploaded in another session/browser
							// Mark as uploaded to prevent future attempts
							this.uploadedHashes.add(fileMetadata.ID);
							this.recentlyUploadedHashes.add(fileMetadata.ID);
							console.log(`ℹ️ File already exists in database (race condition): ${fileMetadata.fileName}`);
							// Don't show error to user - this is expected behavior
							return;
						}
						// Display all GraphQL errors to user
						if (error.graphQLErrors && error.graphQLErrors.length > 0) {
							this.error = 'Neo4j save failed: ' + error.graphQLErrors.map(e => e.message).join('; ');
							console.error('GraphQL errors:', error.graphQLErrors);
						} else {
							this.error = 'Neo4j save failed: ' + (error.message || 'Unknown error');
						}
					})
					// console.log('Image created successfully:', childNods);
				} catch (error) {
					console.error('Upload error:', error);
					throw error;
				}
			},
			async handleFileUpload(file) {
				// get a file from local drive and display it as a thumbnail
				const fileId = await this.computeFileHash(file);
				// Prevent duplicate uploads with debounce lock
				if (this.uploadingFiles.has(fileId)) {
					return; // Already uploading this file
				}
				this.uploadingFiles.add(fileId);
				try {
					// const session = await fetchAuthSession();
					// const tokens = session.tokens;
					// const identityId = session.identityId;
					// if (!tokens?.idToken) {
					// 	throw new Error('No ID token available');
					// }
					const token = localStorage.getItem('user-token') || sessionStorage.getItem('user-token');
					const identityId = this.authStatus.userID;

					let imageMetadata = {};
					if (file.type.startsWith('image/')) {
						imageMetadata = await this.extractImageMetadata(file);
					}
					// const userId = this.authStatus.userID;
					// Deterministic ID based on file content to avoid duplicates
					const fileId = await this.computeFileHash(file);
					const fileExtension = file.name.split('.').pop().toLowerCase();
					// const fileSignature = (file) => `${file.name}:${file.size}:${file.lastModified}`;
					// console.log('File signature:', fileSignature(file));
					const fileKey = `${fileId}.${fileExtension}`;
					// const fileKey = `${userId}.${fileId}.${fileExtension}`;
					const fullS3Key = `${identityId}/${fileKey}`; // actual protected path segment
					// console.log('Uploading file to S3 with key:', fileKey);
					// save image to S3
					try {
						// const uploadResult = await uploadData({
						// 	key: fileKey,
						// 	data: file,
						// 	options: {
						// 		contentType: file.type,
						// 		accessLevel: 'protected',
						// 		defaultAuthMode: 'userPool'
						// 	}
						// });
						// // console.log('Upload successful:', uploadResult);
						// // Some Amplify versions return a task-like object; wait for completion if present.
						// if (uploadResult?.result instanceof Promise) {
						// 	await uploadResult.result;
						// 	// console.log('Upload finalized');
						// }

						const baseUrl = process.env.VUE_APP_GRAPHQL_URL.replace('/graphql', '');
						const formData = new FormData();
						formData.append('file', file);
						formData.append('hash', fileId);

						const response = await fetch(`${baseUrl}/api/upload`, {
							method: 'POST',
							headers: {
								'Authorization': `Bearer ${token}`
							},
							body: formData
						});

						if (!response.ok) throw new Error('Upload failed');

					} catch (uploadErr) {
						console.error('Upload failed:', uploadErr);
						this.error = 'Upload failed: ' + (uploadErr.message || uploadErr);
						throw uploadErr;
					}
					// console.log('Upload complete, S3:', file);
					let latitude = null;
					let longitude = null;
					if (imageMetadata.hasGpsData) {
						latitude = this.convertGpsToDecimal(
							imageMetadata.gpsLatitude, 
							imageMetadata.gpsLatitudeRef
						);
						longitude = this.convertGpsToDecimal(
							imageMetadata.gpsLongitude, 
							imageMetadata.gpsLongitudeRef
						);
					}
					const fileMetadata = {
						createdBy: this.authStatus.userID,
						dateCreated: new Date().toISOString(),
						dateTaken: imageMetadata.dateTaken || new Date(file.lastModified).toISOString(),
						fileName: file.name,
						ID: fileId,
						key: fileKey,
						contentHash: fileId,
						ownerIdentityId: identityId,
						s3Key: fullS3Key,
						type: file.type,
						size: file.size,
						// Use file's last modified date as a fallback for dateTaken
						fileLastModified: new Date(file.lastModified).toISOString(),
						fileUploadDate: new Date().toISOString(),
						latitude: latitude,
						longitude: longitude,
						hasLocationData: imageMetadata.hasGpsData || false
					};
					// Here you would save fileMetadata to your database
					this.createNeo4jImage(fileMetadata)
					return fileMetadata;
				} catch (error) {
					console.error('Upload error:', error);
					this.uploadingFiles.delete(fileId);
					throw error;
				} finally {
					// Remove from uploading set after completion
					setTimeout(() => this.uploadingFiles.delete(fileId), 500);
				}
			},
			convertExifDateToISO(exifDate) {
				// Check if it's already in ISO format
				if (exifDate.includes('T') && exifDate.includes('Z')) {
					return exifDate;
				}
				// Convert from EXIF format (YYYY:MM:DD HH:MM:SS) to ISO
				try {
					// Replace colons in the date part with dashes
					const fixedDateString = exifDate.replace(/^(\d{4}):(\d{2}):(\d{2})/, '$1-$2-$3');
					return new Date(fixedDateString).toISOString();
				} catch (error) {
					console.error('Error converting EXIF date to ISO:', error);
					return null;
				}
			},
			async extractImageMetadata(file) {
				return new Promise((resolve) => {
					const metadata = {
						dateTaken: null,
						gpsLatitude: null,
						gpsLongitude: null,
						gpsLatitudeRef: null,
						gpsLongitudeRef: null,
						hasGpsData: false
					};
					if (!file.type.startsWith('image/')) {
						return resolve(metadata);
					}
					// Fallback initial date
					metadata.dateTaken = new Date(file.lastModified).toISOString();
					const reader = new FileReader();
					reader.onload = (e) => {
						try {
							const buffer = e.target.result;
							let tags = {};
							// Prefer readFromBinaryFile if available; fall back to getData.
							if (typeof EXIF.readFromBinaryFile === 'function') {
								tags = EXIF.readFromBinaryFile(buffer) || {};
							} else if (typeof EXIF.getData === 'function') {
								// Create a blob so EXIF.getData can operate
								const blob = new Blob([buffer]);
								EXIF.getData(blob, function() {
									const all = EXIF.getAllTags(this) || {};
									processTags(all);
									resolve(metadata);
								});
								return; // resolve happens in callback
							}
							processTags(tags);
						} catch (err) {
							console.error('Error reading EXIF data:', err);
						}
						return resolve(metadata);
					};
					const processTags = (tags) => {
						if (!tags || Object.keys(tags).length === 0) return;
						// Pick first valid date hierarchy
						const dateTag = tags.DateTimeOriginal || tags.DateTime || tags.DateTimeDigitized;
						if (dateTag) {
							// Attempt conversion if in EXIF format YYYY:MM:DD HH:MM:SS
							if (/^\d{4}:\d{2}:\d{2}/.test(dateTag)) {
								metadata.dateTaken = new Date(dateTag.replace(/^(\d{4}):(\d{2}):(\d{2})/, '$1-$2-$3')).toISOString();
							} else {
								metadata.dateTaken = dateTag;
							}
						}
						if (tags.GPSLatitude && tags.GPSLongitude) {
							metadata.gpsLatitude = tags.GPSLatitude;
							metadata.gpsLongitude = tags.GPSLongitude;
							metadata.gpsLatitudeRef = tags.GPSLatitudeRef;
							metadata.gpsLongitudeRef = tags.GPSLongitudeRef;
							metadata.hasGpsData = true;
						}
					};
					reader.readAsArrayBuffer(file);
				});
			},
			convertGpsToDecimal(gpsCoord, gpsRef) {
				if (!gpsCoord) return null;
				// EXIF GPS data comes as [degrees, minutes, seconds]
				const degrees = gpsCoord[0];
				const minutes = gpsCoord[1];
				const seconds = gpsCoord[2];
				let decimal = degrees + (minutes / 60) + (seconds / 3600);
				// If southern or western hemisphere, negate the value
				if (gpsRef === 'S' || gpsRef === 'W') {
					decimal = -decimal;
				}
				return decimal;
			},
			handleDrop(e) {
				const files = Array.from(e.dataTransfer.files);
				if (files.length === 0) {
					this.error = 'No files detected. Please drag image files into the drop zone.';
					this.isDragging = false;
					return;
				}
				this.isDragging = false;
				this.handleSelectedFiles(files);
			},
			openFilePicker() {
				// Clear previous state before opening picker
				this.files = [];
				this.thumbnails = [];
				this.fileHashes = [];
				this.error = null;
				
				// Trigger hidden input click - this gives Chrome a fresh context
				if (this.$refs.fileInput) {
					this.$refs.fileInput.click();
				}
			},
			handleFileSelect(e) {
				const input = e.target;
				const fileList = input.files;
				
				if (!fileList || fileList.length === 0) return;
				
				// Convert to array while FileList is valid
				const files = Array.from(fileList);
				
				// Clear immediately
				input.value = '';
				
				this.handleSelectedFiles(files);
			},
			isFileUploaded(hash) {
				if (!hash) return false;
				// Check if the hash is in the uploaded set from server
				return this.uploadedHashes.has(hash);
			},
			isFileUploading(hash) {
				return this.uploadingFiles.has(hash);
			},
			isImageFile(file) {
				return !!(file && file.type && file.type.startsWith('image/'));
			},
			async handleSelectedFiles(files) {
				try {
					this.files = files;
					this.thumbnails = [];
					this.fileHashes = [];
					this.uploadedHashes = new Set(); // reset before re-checking
					this.isCheckingUploads = true;
					// Generate hashes for all files; thumbnails only for images
					for (const file of files) {
						const hash = await this.computeFileHash(file);
						this.fileHashes.push(hash);
						if (file.type.startsWith('image/')) {
							const thumbnail = await this.createThumbnail(file);
							this.thumbnails.push(thumbnail);
						} else {
							this.thumbnails.push(''); // Placeholder for non-image files
						}
					}
					this.error = null;
					
					// Check which files have already been uploaded by querying the server
					if (this.fileHashes.length > 0 && this.userID) {
						await this.checkUploadedFiles();
					}

					// Automatically upload files that are not already on the server
					for (let i = 0; i < this.files.length; i++) {
						const file = this.files[i];
						const hash = this.fileHashes[i];
						if (!this.isFileUploaded(hash)) {
							// Trigger upload without blocking the UI or other uploads
							this.handleFileUpload(file).catch(err => {
								console.error(`Auto-upload failed for ${file.name}:`, err);
							});
						}
					}
				} catch (err) {
					this.error = err.message;
					console.error('File selection error:', err);
				} finally {
					this.isCheckingUploads = false;
				}
			},
			async checkUploadedFiles() {
				try {
					const validHashes = this.fileHashes.filter(h => h !== '');
					if (validHashes.length === 0) return;

					// console.log(`🔍 Starting download status check for ${validHashes.length} files...`);

					if (!this.userID) {
						try {
							// const session = await fetchAuthSession();
							// this.userID = session.tokens.idToken.payload.sub;
							this.userID = this.authStatus.userID;
						} catch (e) {
							console.error('checkUploadedFiles: unable to resolve userID', e);
							return;
						}
					}

					// console.log(`📋 Checking if files have been downloaded previously for user: ${this.userID}`);

					// Add cache bypass and try both hash formats
					const result = await this.$apollo.query({
						query: CheckImagesQuery,
						variables: {
							fileHashes: validHashes,
							userID: this.userID
						},
						fetchPolicy: 'network-only' // Bypass cache to get fresh results
					});

					// console.log('📊 CheckImages query returned:', result.data.CheckImages || []);

					// Handle both hash formats (with and without extensions)
					const uploadedHashesWithoutExt = new Set();
					const uploadedHashesWithExt = new Set();

					// Add hashes without extensions
					(result.data.CheckImages || []).forEach(hash => {
						// Remove extension (.jpg, .jpeg, .png, etc.)
						const hashWithoutExt = hash.replace(/\.[^.]+$/, '');
						uploadedHashesWithoutExt.add(hashWithoutExt);
						uploadedHashesWithExt.add(hash); // Keep original format too
					});

					// Also check against client-side tracking of recently uploaded files
					const clientSideUploaded = new Set([...this.recentlyUploadedHashes]);

					// Combine server and client-side tracking
					const allUploadedHashes = new Set([
						...uploadedHashesWithoutExt,
						...clientSideUploaded
					]);

					// console.log(`📝 Found ${allUploadedHashes.size} previously downloaded files`);

					// Log which files were found as already downloaded
					// allUploadedHashes.forEach((hash) => {
					// 	const fileIndex = this.fileHashes.indexOf(hash);
					// 	if (fileIndex !== -1) {
					// 		const fileName = this.files[fileIndex]?.name || `file_${fileIndex}`;
					// 		console.log(`✅ ${fileName} (hash: ${hash.substring(0, 16)}...) - Already downloaded previously`);
					// 	}
					// });

					// Log which files are new and need to be uploaded
					// const newFiles = validHashes.filter(hash => !allUploadedHashes.has(hash));
					// newFiles.forEach((hash) => {
					// 	const fileIndex = validHashes.indexOf(hash);
					// 	const fileName = this.files[fileIndex]?.name || `file_${fileIndex}`;
					// 	console.log(`🆕 ${fileName} (hash: ${hash.substring(0, 16)}...) - Not downloaded yet, needs upload`);
					// });

					// console.log(`📈 Summary: ${allUploadedHashes.size} downloaded, ${newFiles.length} new files to upload`);

					// Update both server and client-side tracking
					this.uploadedHashes = allUploadedHashes;
				} catch (error) {
					console.error('❌ Error checking uploaded files:', error);
					// Don't block user if check fails
				}
			},
			handleDragEnter(e) {
				e.preventDefault()
				this.isDragging = true
			},
			handleDragLeave(e) {
				e.preventDefault();
				// Only set isDragging to false if we're leaving the drop-zone itself
				if (e.target === e.currentTarget) {
					this.isDragging = false;
				}
			},
			createThumbnail(file) {
				// console.log('Creating thumbnail:', file);
				return new Promise((resolve) => {
					const reader = new FileReader()
					reader.onload = (e) => {
						const img = new Image()
						img.onload = () => {
							// Create canvas for thumbnail
							const canvas = document.createElement('canvas')
							const ctx = canvas.getContext('2d')
							// Set thumbnail size (max 50px width/height while maintaining aspect ratio)
							const maxSize = 50
							let width = img.width
							let height = img.height

							if (width > height) {
								if (width > maxSize) {
									height = Math.round((height * maxSize) / width)
									width = maxSize
								}
							} else {
								if (height > maxSize) {
									width = Math.round((width * maxSize) / height)
									height = maxSize
								}
							}
							canvas.width = width
							canvas.height = height
							// Draw thumbnail
							ctx.drawImage(img, 0, 0, width, height)
							// Convert to data URL
							resolve(canvas.toDataURL(file.type))
						}
						img.src = e.target.result
					}
					reader.readAsDataURL(file)
				})
			},
			downloadFile(file) {
				try {
					const url = URL.createObjectURL(file)
					const a = document.createElement('a')
					a.href = url
					a.download = file.name
					document.body.appendChild(a)
					a.click()
					document.body.removeChild(a)
					URL.revokeObjectURL(url)
				} catch (err) {
					this.error = 'Download failed: ' + err.message
				}
			},
		},
		async mounted() {
				// Get user ID from auth session
				try {
					// const session = await fetchAuthSession();
					// this.userID = session.tokens.idToken.payload.sub;
					this.userID = this.authStatus.userID;
				} catch (e) {
					console.error('Failed to get userID:', e);
				}

				// Prevent file drops outside drop zone from opening in browser
				window.addEventListener('dragover', (e) => {
					e.preventDefault()
					e.stopPropagation()
				})
				window.addEventListener('drop', (e) => {
					e.preventDefault()
					e.stopPropagation()
				})
		},
		async analyzeAndSyncImages() {
			try {
				this.error = null;
				const token = localStorage.getItem('user-token') || sessionStorage.getItem('user-token');
				if (!token) {
					this.error = 'Not authenticated';
					return;
				}

				const baseUrl = process.env.VUE_APP_GRAPHQL_URL.replace('/graphql', '');

				const response = await fetch(`${baseUrl}/api/analyze-and-sync-images`, {
					method: 'POST',
					headers: {
						'Authorization': `Bearer ${token}`,
						'Content-Type': 'application/json'
					}
				});

				if (!response.ok) {
					throw new Error(`Analysis failed: ${response.status} ${response.statusText}`);
				}

				const result = await response.json();

				if (result.success) {
					console.log('🎉 Analysis completed:', result.summary);
					return result;
				} else {
					throw new Error(result.message || 'Analysis failed');
				}

			} catch (error) {
				console.error('Analysis error:', error);
				this.error = `Analysis failed: ${error.message}`;
				throw error;
			}
		},
		async runAnalysis() {
			try {
				this.isAnalyzing = true;
				this.error = null;

				const result = await this.analyzeAndSyncImages();
				this.syncResults = result;

				// Refresh the uploaded files list after analysis
				if (this.fileHashes.length > 0 && this.userID) {
					await this.checkUploadedFiles();
				}

			} catch (error) {
				console.error('Run analysis error:', error);
				this.error = `Analysis failed: ${error.message}`;
			} finally {
				this.isAnalyzing = false;
			}
		},
		beforeUnmount() {
			window.removeEventListener('dragover', (e) => {
				e.preventDefault()
				e.stopPropagation()
			})
		},
	}
</script>

<style scoped>
	.error {
		color: red;
		margin-top: 10px;
	}

	.button-select-images {
		padding: 2px 8px;
	}

	.button-select-images:hover{
		background-color: #a6f4e5;
	}

	.thumbnail-container {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
		gap: 1rem;
		margin-top: 1rem;
		}
	.drop-zone {
		border: 2px dashed #ccc;
		border-radius: 4px;
		padding: 20px;
		text-align: center;
		background: #f8f8f8;
		transition: border .3s ease-in-out;
	}

	.drop-zone:hover,
	.drop-zone.drag-over {
		border-color: #2196f3;
		background: #e3f2fd;
	}

	ul {
		list-style: none;
		padding: 0;
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
		gap: 1rem;
		margin-top: 1rem;
	}

	li {
		display: flex;
		flex-direction: column;
		align-items: center;
		padding: 1px;
		border: 1px solid #ddd;
		border-radius: 4px;
		width: fit-content;
		min-width: 80px;
	}

	.thumbnail {
		width: 50px;
		height: 50px;
		object-fit: cover;
		border-radius: 4px;
		cursor: pointer;
	}

	.file-info {
		margin-top: 0.5rem;
		text-align: center;
		width: 100%;
		max-width: 100px;
	}

	.file-info span {
		display: block;
		font-size: 0.8rem;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		margin-bottom: 0.5rem;
	}

	.file-info button {
		padding: 1px;
		background: #2196f3;
		color: white;
		border: none;
		border-radius: 4px;
		cursor: pointer;
		margin: 0 auto;
	}

	.file-info button:hover {
		background: #1976d2;
	}

	.file-info button:disabled {
		background: #ccc;
		cursor: not-allowed;
		opacity: 0.3;
	}

	.uploaded-icon {
		display: none;
	}

	.thumb-wrapper {
		position: relative;
		display: inline-block;
	}

	.uploaded-badge {
		position: absolute;
		top: 4px;
		right: 4px;
		background: #4caf50;
		color: white;
		border-radius: 50%;
		width: 20px;
		height: 20px;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 0.9rem;
		font-weight: bold;
		box-shadow: 0 0 4px rgba(0,0,0,0.3);
	}

	.no-preview {
		width: 96px;
		height: 96px;
		display: flex;
		align-items: center;
		justify-content: center;
		text-align: center;
		font-size: 0.75rem;
		color: #546e7a;
		border: 1px dashed #cfd8dc;
		border-radius: 4px;
		background: #f7f9fa;
		padding: 4px;
	}

	.uploaded-text {
		display: block;
		font-size: 0.8rem;
		color: #4caf50;
		font-weight: bold;
		text-align: center;
	}

	li.uploaded {
		opacity: 0.3;
	}

	.s3-list-wrapper {
		margin-top: 1rem;
		text-align: left;
	}

	.s3-list {
		list-style: none;
		padding: 0;
		margin: 0;
		max-height: 200px;
		overflow-y: auto;
		border: 1px solid #ddd;
		border-radius: 4px;
		background: #fafafa;
	}

	.s3-item {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 4px 6px;
		border-bottom: 1px solid #eee;
		font-size: 0.75rem;
	}

	.file-button {
		padding: 8px 16px;
		background: #2196f3;
		color: white;
		border: none;
		border-radius: 4px;
		cursor: pointer;
		font-size: 1rem;
		margin-bottom: 10px;
	}

	.file-button:hover {
		background: #1976d2;
	}

	.file-button:active {
		background: #1565c0;
	}
	.s3-item:last-child { border-bottom: none; }

	.s3-key {
		flex: 1;
		margin-right: 6px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.s3-download {
		padding: 2px 6px;
		background: #4caf50;
		color: #fff;
		border: none;
		border-radius: 4px;
		cursor: pointer;
		font-size: 0.65rem;
	}

	.s3-download:hover { background: #3e8e41; }
</style>